import { createContext, useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSearchParams } from 'react-router-dom'

const ConfigContext = createContext()

const defaultConfig = {
  mode: 'multi-district',
  focused_district: null,
  app_title: 'Uganda Facilities Dashboard',
  default_view: 'map',
  default_category: 'health',
  available_categories: ['health'],
  url_routing: {
    enable_district_routing: true,
    preserve_query_params: true,
  },
}

export function ConfigProvider({ children }) {
  const [config, setConfig] = useState(defaultConfig)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchParams] = useSearchParams()

  useEffect(() => {
    loadConfiguration()
  }, [])

  const loadConfiguration = async () => {
    try {
      setLoading(true)
      const response = await fetch('/data/app_config.json')
      if (!response.ok) {
        throw new Error(`Failed to load configuration: ${response.status}`)
      }

      const configData = await response.json()

      // Override with URL parameters if present
      const urlOverrides = getConfigFromURL()
      const finalConfig = { ...configData, ...urlOverrides }

      setConfig(finalConfig)
      setError(null)
    } catch (err) {
      // Error loading app configuration - using defaults
      setError(err.message)
      // Fall back to default configuration
      setConfig(defaultConfig)
    } finally {
      setLoading(false)
    }
  }

  const getConfigFromURL = () => {
    const overrides = {}

    // Check for mode override
    const urlMode = searchParams.get('mode')
    if (urlMode === 'single-district' || urlMode === 'multi-district') {
      overrides.mode = urlMode
    }

    // Check for focused district override
    const urlDistrict = searchParams.get('focused_district')
    if (urlDistrict) {
      overrides.focused_district = urlDistrict
      // If a district is specified via URL, switch to single-district mode
      if (!urlMode) {
        overrides.mode = 'single-district'
      }
    }

    return overrides
  }

  const isMultiDistrictMode = () => {
    return config.mode === 'multi-district'
  }

  const isSingleDistrictMode = () => {
    return config.mode === 'single-district'
  }

  const getFocusedDistrict = () => {
    return config.focused_district
  }

  const getAppTitle = () => {
    if (isSingleDistrictMode() && config.focused_district) {
      // Capitalize district name for title
      const districtName =
        config.focused_district.split('-')[1].charAt(0).toUpperCase() +
        config.focused_district.split('-')[1].slice(1)
      return `${districtName} District Dashboard`
    }
    return config.app_title
  }

  const shouldShowAllDistrictsOption = () => {
    return isMultiDistrictMode()
  }

  const getDefaultLocationSelection = () => {
    if (isSingleDistrictMode() && config.focused_district) {
      return {
        district: config.focused_district,
        subcounty: null,
        parish: null,
        village: null,
      }
    }
    return {
      district: null, // "All Districts" in multi-district mode
      subcounty: null,
      parish: null,
      village: null,
    }
  }

  const getMaxLocationDepth = () => {
    return config.max_location_depth || 'village' // Default to village if not specified
  }

  const isLocationDepthAllowed = depth => {
    const maxDepth = getMaxLocationDepth()
    const depths = ['district', 'subcounty', 'parish', 'village']
    const maxIndex = depths.indexOf(maxDepth)
    const currentIndex = depths.indexOf(depth)
    return currentIndex <= maxIndex
  }

  const getLocationDepthOrder = () => {
    const maxDepth = getMaxLocationDepth()
    const depths = ['district', 'subcounty', 'parish', 'village']
    const maxIndex = depths.indexOf(maxDepth)
    return depths.slice(0, maxIndex + 1)
  }

  const getDefaultCategory = () => {
    return config.default_category || 'health' // Default to health if not specified
  }

  const value = {
    config,
    loading,
    error,
    isMultiDistrictMode,
    isSingleDistrictMode,
    getFocusedDistrict,
    getAppTitle,
    shouldShowAllDistrictsOption,
    getDefaultLocationSelection,
    getMaxLocationDepth,
    isLocationDepthAllowed,
    getLocationDepthOrder,
    getDefaultCategory,
    reloadConfig: loadConfiguration,
  }

  return (
    <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
  )
}

export function useConfig() {
  const context = useContext(ConfigContext)
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider')
  }
  return context
}

ConfigProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
